import { apiClient, fetchWithDeduplication, getAuthToken, resolveApiUrl } from '@/lib/db';
import { getCurrentLang } from '@/i18n';
import type { Page, UserProfile } from './types';

/** JSON body from `GET /api/user-page/:userId/:slug` (with optional `?lang=`). */
export type FetchUserPageResponse = {
    page: Page;
    userProfile: UserProfile;
    childPages?: { id: string; title: string; slug: string }[];
};

export type RootPageContent = {
    pages: Record<string, any>;
    version: string;
    lastUpdated: number;
};

export type PageRouteParams = {
    owner: string;
    slug: string;
    orgSlug?: string;
};

export type PageEmailPreviewUrlParams = PageRouteParams & {
    token?: string | null;
    lang?: string;
};

const encodeSegment = (value: string) => encodeURIComponent(value);

export const getUserPageApiPath = (identifier: string, slug: string, lang?: string) => {
    const params = new URLSearchParams();
    if (lang) params.set('lang', lang);
    const qs = params.toString();
    return `/api/user-page/${encodeSegment(identifier)}/${encodeSegment(slug)}${qs ? `?${qs}` : ''}`;
};

export const getPageLayoutId = (pageId: string) => pageId.startsWith('page-') ? pageId : `page-${pageId}`;

export const buildPageRootContent = (layoutId: string, layout: any): RootPageContent => ({
    pages: { [layoutId]: layout },
    version: '1.0.0',
    lastUpdated: Date.now(),
});

export const getUserPageRoutePath = ({ owner, slug, orgSlug }: PageRouteParams) => {
    const ownerSegment = encodeSegment(owner);
    const slugSegment = encodeSegment(slug);
    return orgSlug
        ? `/org/${encodeSegment(orgSlug)}/user/${ownerSegment}/pages/${slugSegment}`
        : `/user/${ownerSegment}/pages/${slugSegment}`;
};

export const getPageEmailPreviewPath = (page: PageRouteParams) => {
    return `${getUserPageRoutePath(page)}/email-preview`;
};

export const getPageEmailSendPath = (page: PageRouteParams, lang?: string) => {
    const params = new URLSearchParams();
    if (lang) params.set('lang', lang);
    const qs = params.toString();
    return `${getUserPageRoutePath(page)}/email-send${qs ? `?${qs}` : ''}`;
};

export const getPageEmailPreviewUrl = ({ token, lang, ...page }: PageEmailPreviewUrlParams) => {
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (lang) params.set('lang', lang);
    const qs = params.toString();
    return resolveApiUrl(`${getPageEmailPreviewPath(page)}${qs ? `?${qs}` : ''}`);
};

export const fetchUserPage = async (
    userId: string,
    slug: string
): Promise<FetchUserPageResponse | null> => {
    const key = `user-page-${userId}-${slug}`;
    return fetchWithDeduplication<FetchUserPageResponse | null>(key, async () => {
        const lang = getCurrentLang();
        const result = await apiClient<FetchUserPageResponse>(
            getUserPageApiPath(userId, slug, lang && lang !== 'en' ? lang : undefined)
        );
        return result ?? null;
    }, 10);
};

export const fetchOriginalUserPage = async (
    userId: string,
    slug: string
): Promise<FetchUserPageResponse> => {
    return apiClient<FetchUserPageResponse>(getUserPageApiPath(userId, slug));
};

export const fetchPageById = async (id: string) => {
    return fetchWithDeduplication<Page | null>(`page-${id}`, async () => {
        const result = await fetchPageDetailsById(id);
        return result?.page ?? null;
    });
};

export const fetchUserPages = async (userId: string) => {
    const key = `user-pages-${userId}`;
    return fetchWithDeduplication(key, async () => {
        return apiClient(`/api/pages?userId=${userId}`);
    });
};

export const fetchPageDetailsById = async (pageId: string) => {
    const key = `page-details-${pageId}`;
    return fetchWithDeduplication<FetchUserPageResponse | null>(key, async () => {
        const result = await apiClient<FetchUserPageResponse>(getUserPageApiPath(pageId, pageId));
        return result ?? null;
    });
};

export const createPage = async (pageData: any): Promise<any> => {
    return apiClient('/api/pages', {
        method: 'POST',
        body: JSON.stringify(pageData),
    });
};

export const updatePage = async (pageId: string, updates: any): Promise<any> => {
    return apiClient(`/api/pages/${pageId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
    });
};

export const deletePage = async (pageId: string): Promise<void> => {
    return apiClient(`/api/pages/${pageId}`, { method: 'DELETE' });
};

export const updatePageMeta = async (pageId: string, metaUpdates: any) => {
    const result = await fetchPageDetailsById(pageId);
    if (!result?.page) throw new Error('Page not found');
    const newMeta = { ...(result.page.meta ?? {}), ...metaUpdates };
    return updatePage(pageId, { meta: newMeta });
};

// --- Page Versions ---

export const fetchPageVersions = async (pageId: string) => {
    return apiClient(`/api/pages/${pageId}/versions`);
};

export const createPageVersion = async (pageId: string, label?: string) => {
    return apiClient(`/api/pages/${pageId}/versions`, {
        method: 'POST',
        body: JSON.stringify({ label }),
    });
};

export const deletePageVersion = async (pageId: string, versionId: string) => {
    return apiClient(`/api/pages/${pageId}/versions/${versionId}`, { method: 'DELETE' });
};

export const restorePageVersion = async (pageId: string, versionId: string) => {
    return apiClient(`/api/pages/${pageId}/versions/${versionId}/restore`, { method: 'POST' });
};

export const sendUserPageEmail = async (
    page: PageRouteParams,
    body: { to: string; subject?: string },
    lang?: string
) => {
    return apiClient<{ success: boolean; messageId?: string; error?: string }>(getPageEmailSendPath(page, lang), {
        method: 'POST',
        body: JSON.stringify(body),
    });
};

export const fetchPageEmailPreviewBlob = async (page: PageRouteParams): Promise<Blob | null> => {
    const token = await getAuthToken();
    const response = await fetch(resolveApiUrl(getPageEmailPreviewPath(page)), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) return null;
    return response.blob();
};
